/**
 * @license Siftrock
 * (c) 2014-2018 Siftrock, Inc. http://siftrock.com
 * Unauthorized copying of this file, via any medium is strictly prohibited
 */

import { Component, OnInit, Input, Output } from '@angular/core';

import { Subject } from 'rxjs/Subject';
import { Observable } from 'rxjs/Observable';

import { CacheService } from '../../../global/ng2-cache/services/cache.service';
import { Partner, Constants } from '../../../global/constants';
import { PartnerField, PartnerList, PartnerSiftrockFieldMap, PartnerStaticFieldMap, AutomateTask } from '../../../global/models';
import { ClassifierService } from '../../../services/classifier.service';
import { PartnerService } from '../../../services/partner.service';
import { GlobalMessageService } from '../../../services/global-message.service';
import { ConfirmDialogComponent } from '../../../global/confirm-dialog/confirm-dialog.component';
import { BsModalService } from 'ngx-bootstrap';

@Component({
  selector: 'app-respondtrack-task',
  templateUrl: './respondtrack-task.component.html'
})
export class RespondtrackTaskComponent implements OnInit {
  @Input() task: AutomateTask;
  @Output() onChange = new Subject<AutomateTask>();
  @Output() onNavChange = new Subject<boolean>();

  session: any;
  loadingPage: boolean = false;

  classifier: any = null;

  // form state management
  availableCategory: any[] = [];
  unusedCategories: boolean = true;
  editMode: boolean[] = [];

  selectedPartner: Partner;
  selectedList: Array<PartnerList> = [];

  staticFieldMap: Array<Array<PartnerStaticFieldMap>> = [];
  siftrockFieldMap: Array<Array<PartnerSiftrockFieldMap>> = [];

  loadingFields: boolean = false;
  partnerFields: Array<PartnerField>;
  usedFields: Array<Array<PartnerField>> = [];

  constructor(private cacheService: CacheService,
              private constants: Constants,
              private globalMessageService: GlobalMessageService,
              private classifierService: ClassifierService,
              private partnerService: PartnerService,
              private modalService: BsModalService) { }

  ngOnInit() {
    // should already be set from skill-detail
    this.session = this.cacheService.get('session');
    this.initView();
  }

  changePartner(partner: Partner) {
    if (this.selectedPartner) {
      let me = this;
      let message = `Confirm you'd like to change integration partners. This action will remove all list and field mapping for this skill.`;
      if (!partner) {
        message = `Confirm you'd like to clear this integration partner. This action will remove all list and field mapping for this skill.`;
      }
      this.modalService.show(ConfirmDialogComponent, Object.assign({}, {
        initialState: {
          message: message,
          confirm: function() {
            me.selectedPartner = partner;
            me.task.settings.partner = partner ? partner.name : '';
            if (me.task.settings.partner.length > 0) {
              me.task.settings.categories.forEach(category => {
                category.list = {};
                category.siftrock_fields = [];
                category.static_fields = [];
              });
            }
            else {
              me.task.settings.categories = [];
            }

            me.editMode.forEach(em => {
              em = false;
            });

            this.selectedList = [];
            this.staticFieldMap = [];
            this.siftrockFieldMap = [];
            me.loadFields();
            me.stage();
            me.globalMessageService.message(`Changes were saved`);
          }
        }
      }));
    }
    else {
      this.selectedPartner = partner;
      this.task.settings.partner = partner.name;
      this.editMode.forEach(em => {
        em = false;
      });
      this.selectedList = [];
      this.staticFieldMap = [];
      this.siftrockFieldMap = [];
      this.loadFields();
      this.stage();
    }
  }

  toggleEdit(index: number) {
    this.editMode[index] = !this.editMode[index];
  }

  categoryName(categoryId: string) {
    return this.classifier.category.find(category => {return category.id == categoryId}).name;
  }

  addResponseOption() {
    let cat: any;
    // find the first unused category and push it into the state array
    for (let idx=0; idx<this.classifier.category.length; idx++) {
      if (this.task.settings.categories.findIndex(category => { return category.categoryId == this.classifier.category[idx].id }) < 0) {
        cat = this.classifier.category[idx];
        break;
      }
    }

    this.task.settings.categories.push({
      categoryId: cat.id,
      list: {},
      siftrock_fields: [],
      static_fields: []
    });

    this.editMode.push(true);
    this.usedFields.push([]);
    this.setupCategories();
    this.stage();
  }

  removeResponseOption(index: number) {
    let me = this;
    this.modalService.show(ConfirmDialogComponent, Object.assign({}, {
      initialState: {
        message: `Confirm you'd like to remove the selected bot track category`,
        confirm: function() {
          me.task.settings.categories.splice(index, 1);
          me.setupView();
          me.stage();
          me.globalMessageService.message(`Track category was removed`);
        }
      }
    }));
  }

  changeList(list: any, index: number) {
    this.task.settings.categories[index].list = list ? list : {};
    this.stage();
  }

  changeStaticField(fields: Array<PartnerStaticFieldMap>, index: number) {
    this.task.settings.categories[index].static_fields = [];
    if (fields.length > 0) {
      fields.forEach(f => {
        this.task.settings.categories[index].static_fields.push({
          id: f.partnerField.id,
          display: f.partnerField.display,
          name: f.partnerField.name,
          type: f.partnerField.type,
          value: f.value
        });
      });
    }

    this.setUsedFields();
    this.stage();
  }

  changeSiftrockField(fields: Array<PartnerSiftrockFieldMap>, index: number) {
    this.task.settings.categories[index].siftrock_fields = [];
    if (fields.length > 0) {
      fields.forEach(f => {
        this.task.settings.categories[index].siftrock_fields.push({
          id: f.partnerField.id,
          display: f.partnerField.display,
          name: f.partnerField.name,
          type: f.partnerField.type,
          value: f.siftrockField.fieldName
        });
      });
    }

    this.setUsedFields();
    this.stage();
  }

  refreshFields() {
    this.loadFields(true);
  }

  /**
   * Keep track of used fields so they can't be mapped multiple times
   */
  private setUsedFields() {
    this.usedFields = [];
    for (let idx=0; idx<this.task.settings.categories.length; idx++) {
      this.usedFields[idx] = [];
      if (this.staticFieldMap[idx]) {
        this.usedFields[idx] = this.usedFields[idx].concat(this.staticFieldMap[idx].map(sf => { return sf.partnerField}));
      }
      if (this.siftrockFieldMap[idx]) {
        this.usedFields[idx] = this.usedFields[idx].concat(this.siftrockFieldMap[idx].map(sf => { return sf.partnerField}));
      }
    }
  }

  private initView() {
    this.loadingPage = true;

    for (let idx=0; idx<this.task.settings.categories.length; idx++) {
      this.editMode.push(false);
      this.usedFields[idx] = [];
      this.staticFieldMap[idx] = [];
      this.siftrockFieldMap[idx] = [];
      if (this.task.settings.categories[idx].list.id) {
        this.selectedList[idx] = {
          id: this.task.settings.categories[idx].list.id,
          name: this.task.settings.categories[idx].list.name
        };
      }

      if (this.task.settings.categories[idx].static_fields.length > 0) {
        this.task.settings.categories[idx].static_fields.forEach(f => {
          this.staticFieldMap[idx].push({
            partnerField: {
              id: f.id,
              name: f.name,
              display: f.display,
              type: f.type
            },
            value: f.value
          });
        });
      }

      if (this.task.settings.categories[idx].siftrock_fields.length > 0) {
        this.task.settings.categories[idx].siftrock_fields.forEach(f => {
          let srField = this.constants.siftrockFields.find(sf => { return sf.fieldName == f.value; });
          this.siftrockFieldMap[idx].push({
            partnerField: {
              id: f.id,
              name: f.name,
              display: f.display,
              type: f.type
            },
            siftrockField: srField
          });
        });
      }
    }

    Observable.forkJoin(
      this.classifierService.getClassifier(this.task.settings.classifier_id)
    ).subscribe(
      (results: any) => {
        this.classifier = results[0];

        // set up available categories (note this gets done again post setupView to get rid of used items)
        this.availableCategory = JSON.parse(JSON.stringify(this.classifier.category));
        this.availableCategory.forEach(ac => { ac.used = false });

        //this.setUsedFields();
        this.setupView();
        this.loadFields();
        this.validate();
        this.loadingPage = false;
      },
      e => {
        this.globalMessageService.error();
      }
    );
  }

  private setupView() {
    if (this.task.settings.partner.length > 0) {
      this.selectedPartner = this.constants.partners.find(p => { return p.name == this.task.settings.partner});
    }
    this.setupCategories();
  }

  /**
   * Sets up categories for availability in dropdowns
   */
  private setupCategories() {
    // start with a baseline of available categories
    this.availableCategory = JSON.parse(JSON.stringify(this.classifier.category));
    this.availableCategory.forEach(ac => { ac.used = false });

    // remove any that are already used
    this.task.settings.categories.forEach(response => {
      let index = this.availableCategory.findIndex(ac => { return ac.id == response.categoryId});
      if (index >= 0) {
        this.availableCategory[index].used = true;
      }
    });

    // no more categories left, don't show the Add Response button
    if (this.classifier.category.length == this.task.settings.categories.length) {
      this.unusedCategories = false;
    }
    else {
      this.unusedCategories = true;
    }
  }

  private loadFields(refreshFromPartner: boolean = false) {
    this.partnerFields = [];
    if (this.selectedPartner) {
      this.loadingFields = true;

      if (refreshFromPartner) {
        this.partnerService.clearFieldCache(this.selectedPartner.name);
      }

      this.partnerService.getFields(this.selectedPartner.name).subscribe(
        (result: any) => {
          result.forEach(field => {
            this.partnerFields.push({
              id: field.id,
              name: field.name,
              display: field.description,
              type: field.type
            });
          });

          this.partnerFields.sort((a, b) => {
            if (a.name < b.name) {
              return -1;
            }
            else {
              return 1;
            }
          });
          this.setUsedFields();
          this.loadingFields = false;
        },
        error => {
          this.loadingFields = false;
        },
      );
    }
  }

  /**
   * Validate task requirements are met and set
   */
  private validate() {
    this.task.enabled = true;
    this.task.valid = true;
    this.task.invalid_reason = '';

    if (this.task.settings.partner && this.task.settings.partner.length > 0) {
      if (this.task.settings.categories.length == 0) {
        this.task.valid = false;
        this.task.invalid_reason = 'At least one response option must be selected';
      }
    }

    this.task.settings.categories.forEach(cat => {
      let totFields = cat.siftrock_fields.length + cat.static_fields.length;
      if (!cat.list.id && totFields == 0) {
        this.task.valid = false;
        this.task.invalid_reason = 'A list or at least one field mapping must be set for tracking';
      }
    });
  }

  /**
   * Stage changes to be saved
   */
  private stage() {
    this.validate();
    this.task.updated_at = new Date();
    this.onChange.next(this.task);
  }
}



// WEBPACK FOOTER //
// ./client/app/automate/tasks/respondtrack-task/respondtrack-task.component.ts